// ExoWare's own checkout page. Stripe renders the payment fields inside its iframes (card data never touches us);
// everything around them is ours. Uses a Checkout Session in ui_mode "elements".
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { AnimatePresence, motion } from "motion/react";
import { loadStripe, type Appearance, type StripeCheckoutElementsSdk } from "@stripe/stripe-js";
import { api, ApiError, useApi } from "../api.ts";
import { useMe } from "../auth.tsx";
import { fmtExpiry, fmtMoney } from "../format.ts";
import { Button, cn, ErrorText, Icon, Input, Switch } from "../ui/index.tsx";
import { LogoMark, Wordmark } from "../ui/logo.tsx";
import { GameScene } from "../three/GameScene.tsx";
import { themeFor, variantFor } from "../three/themes.ts";
import { CompatBadges, GameArt, NotForSale } from "./_shared.tsx";
import { EASE } from "./member/kit.tsx";
import type { BillingConfig, Bundle, CheckoutItem, CheckoutQuote, CheckoutResponse, Game } from "../../shared/types.ts";

const appearance: Appearance = {
  theme: "night",
  variables: {
    colorPrimary: "#ff3d4f",
    colorBackground: "#0b0b10",
    colorText: "#f1f1f4",
    colorTextSecondary: "#8f8f9c",
    colorTextPlaceholder: "#5a5a68",
    colorDanger: "#ff4d4d",
    colorSuccess: "#2ee58a",
    fontFamily: "'Instrument Sans', system-ui, sans-serif",
    fontSizeBase: "15px",
    borderRadius: "10px",
    spacingUnit: "4px",
    focusBoxShadow: "0 0 0 3px rgba(255,61,79,0.25)",
    focusOutline: "none",
  },
  rules: {
    ".Input": { border: "1px solid #23232d", boxShadow: "inset 0 1px 2px rgba(0,0,0,0.4)", padding: "12px 14px" },
    ".Input:focus": { border: "1px solid #ff3d4f" },
    ".Tab": { border: "1px solid #23232d", backgroundColor: "#111117" },
    ".Tab:hover": { border: "1px solid #33333f" },
    ".Tab--selected": { border: "1px solid #ff3d4f", backgroundColor: "rgba(255,61,79,0.08)", boxShadow: "0 0 18px -6px rgba(255,61,79,0.7)", color: "#ffffff" },
    ".Tab--selected:hover": { color: "#ffffff" },
    ".TabLabel--selected": { color: "#ffffff" },
    ".TabIcon--selected": { fill: "#ffffff", color: "#ffffff" },
    ".Label": { fontSize: "12px", fontWeight: "500", color: "#8f8f9c" },
  },
};
const fonts = [{ cssSrc: "https://fonts.googleapis.com/css2?family=Instrument+Sans:wght@400;500;600&display=swap" }];

/** One Stripe.js instance per publishable key for the page's lifetime. */
const stripeCache = new Map<string, ReturnType<typeof loadStripe>>();
const getStripe = (pk: string) => { if (!stripeCache.has(pk)) stripeCache.set(pk, loadStripe(pk)); return stripeCache.get(pk)!; };

/** The price as charged: struck-through list price when a promo applies. Falls back to the display price before the first quote. */
function PriceTag({ quote, fallback, className }: { quote: CheckoutQuote | null; fallback?: string; className?: string }) {
  const promo = quote?.promo && quote.amount_cents < quote.list_cents;
  const text = quote ? fmtMoney(quote.amount_cents, quote.currency.toUpperCase()) : fallback;
  return (
    <div className={cn("text-right", className)}>
      <AnimatePresence mode="popLayout">
        <motion.div key={text} initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
          className={cn("font-display text-3xl font-semibold tabular-nums", promo && "text-ok")}>{text}</motion.div>
      </AnimatePresence>
      {promo && <div className="text-sm text-faint line-through tabular-nums">{fmtMoney(quote.list_cents, quote.currency.toUpperCase())}</div>}
    </div>
  );
}

const PERKS = (gift: boolean, username: string) => [
  ["zap", gift ? "You get a licence key the moment payment clears" : "Unlocks instantly on your account the moment payment clears"],
  ["refresh", gift ? "Whoever redeems it gets the time added to their account" : "Time stacks: buying again adds to whatever is left"],
  ["user", gift ? <>Paid by <b className="text-fg">{username}</b>, for anyone you choose</> : <>Goes to <b className="text-fg">{username}</b></>],
] as const;

function Perks({ gift }: { gift: boolean }) {
  const me = useMe();
  return (
    <ul className="mt-7 space-y-2.5 text-sm">
      {PERKS(gift, me.username).map(([icon, text], i) => (
        <motion.li key={`${gift}-${i}`} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + i * 0.08, ease: EASE }}
          className="flex items-center gap-3 text-muted">
          <span className="grid size-7 place-items-center rounded-lg bg-white/[0.04] text-accent"><Icon name={icon as "zap"} className="size-3.5" /></span>{text}
        </motion.li>
      ))}
    </ul>
  );
}

function OrderSummary({ game, tierIndex, onTier, busy, quote, gift }: { game: Game; tierIndex: number; onTier: (i: number) => void; busy: boolean; quote: CheckoutQuote | null; gift: boolean }) {
  const me = useMe();
  const tier = game.pricing_tiers[tierIndex];
  const owned = !gift && me.entitlements.find((e) => e.game_id === game.id && e.active);
  return (
    <div className="relative z-10 flex h-full flex-col">
      <Link to={`/games/${game.id}`} className="mb-10 inline-flex w-fit items-center gap-2 text-sm text-muted hover:text-fg">
        <Icon name="chevronLeft" className="size-4" /> Back to {game.name}
      </Link>
      <div className="kicker mb-3 text-accent">// order summary</div>
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}
        className="pane hud hud-accent overflow-hidden">
        <GameArt game={game} className="aspect-[460/215]" />
        <div className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold leading-tight">{game.name}</h1>
              <p className="mt-1 text-sm text-muted">{gift ? "Gift key: " : ""}{tier?.dur} of access{owned ? `, added on top of what you have (${fmtExpiry(owned.expires_at)})` : ""}</p>
              <CompatBadges game={game} className="mt-2.5" />
            </div>
            <PriceTag quote={quote} fallback={tier?.price} />
          </div>
        </div>
      </motion.div>

      <div className="kicker mb-2 mt-7">// duration</div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {game.pricing_tiers.map((t, i) => (
          <button key={`${t.dur}-${i}`} type="button" disabled={busy} onClick={() => onTier(i)}
            className={cn("relative rounded-xl border px-3 py-2.5 text-left transition-all disabled:opacity-60",
              i === tierIndex ? "border-accent bg-accent-soft shadow-[0_0_24px_-8px_var(--color-accent)]" : "border-line bg-surface/60 hover:border-line-strong")}>
            {/lifetime/i.test(t.dur) && <span className="absolute -top-2 right-2 rounded bg-accent px-1 text-[9px] font-semibold uppercase leading-4 text-accent-fg">Best</span>}
            <div className="text-xs text-muted">{t.dur}</div>
            <div className="font-display text-lg font-semibold tabular-nums">{t.price}</div>
          </button>
        ))}
      </div>

      <Perks gift={gift} />
      {game.features.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-1.5">
          {game.features.slice(0, 8).map((f) => <span key={f} className="rounded-md border border-line bg-bg/60 px-2 py-0.5 text-xs text-muted">{f}</span>)}
        </div>
      )}
    </div>
  );
}

function BundleSummary({ bundle, quote, gift }: { bundle: Bundle; quote: CheckoutQuote | null; gift: boolean }) {
  const games = useApi<Game[]>("/games");
  const included = bundle.games.map((id) => games.data?.find((g) => g.id === id)).filter((g): g is Game => !!g);
  return (
    <div className="relative z-10 flex h-full flex-col">
      <Link to="/pricing#bundles" className="mb-10 inline-flex w-fit items-center gap-2 text-sm text-muted hover:text-fg">
        <Icon name="chevronLeft" className="size-4" /> Back to pricing
      </Link>
      <div className="kicker mb-3 text-accent">// order summary · bundle</div>
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }} className="pane hud hud-accent overflow-hidden">
        <div className="grid grid-cols-2 gap-px bg-line">
          {included.slice(0, 4).map((g, i) => (
            <motion.div key={g.id} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1 + i * 0.07, ease: EASE }}>
              <GameArt game={g} className="aspect-[460/215]" />
            </motion.div>
          ))}
        </div>
        <div className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold leading-tight">{bundle.name}</h1>
              <p className="mt-1 text-sm text-muted">{gift ? "Gift key: " : ""}{bundle.days === null ? "Lifetime" : `${bundle.days} days`} of access to {bundle.games.length} games</p>
            </div>
            <PriceTag quote={quote} fallback={bundle.price} />
          </div>
          {bundle.description && <p className="mt-3 text-sm text-muted">{bundle.description}</p>}
          <ul className="mt-4 flex flex-wrap gap-1.5">
            {bundle.game_names.map((n) => <li key={n} className="rounded-md border border-line bg-bg/60 px-2 py-0.5 text-xs">{n}</li>)}
          </ul>
        </div>
      </motion.div>
      <Perks gift={gift} />
    </div>
  );
}

/** "Have a code?" field. Checks the code with the server first; the session is only rebuilt for a code that works. */
function PromoField({ item, applied, onApply }: { item: CheckoutItem; applied: string | null; onApply: (code: string | null) => void }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const apply = async () => {
    if (!code.trim()) return;
    setBusy(true); setError(null);
    try {
      await api.post<CheckoutQuote>("/checkout/quote", { ...item, promo_code: code.trim() });
      onApply(code.trim().toUpperCase());
      setCode(""); setOpen(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally { setBusy(false); }
  };
  if (applied) {
    return (
      <div className="mb-4 flex items-center justify-between rounded-lg border border-ok/30 bg-ok/5 px-3 py-2 text-sm">
        <span className="flex items-center gap-2 text-ok"><Icon name="check" className="size-4" />Code <b className="font-mono">{applied}</b> applied</span>
        <button type="button" onClick={() => onApply(null)} className="text-xs text-muted hover:text-fg">Remove</button>
      </div>
    );
  }
  return (
    <div className="mb-4">
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} className="text-sm text-muted underline decoration-line-strong underline-offset-4 hover:text-fg">Have a promo code?</button>
      ) : (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="overflow-hidden">
          <div className="flex gap-2">
            <Input autoFocus value={code} onChange={(e) => setCode(e.target.value)} placeholder="CODE" className="h-11 font-mono uppercase"
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void apply(); } }} />
            <Button type="button" onClick={() => void apply()} loading={busy} className="h-11">Apply</Button>
          </div>
          <div className="mt-1.5"><ErrorText>{error}</ErrorText></div>
        </motion.div>
      )}
    </div>
  );
}

function PaymentPanel({ item, gift, onGift, promo, onPromo, onQuote, onBusy }: {
  item: CheckoutItem; gift: boolean; onGift: (g: boolean) => void; promo: string | null; onPromo: (c: string | null) => void;
  onQuote: (q: CheckoutQuote | null) => void; onBusy: (b: boolean) => void;
}) {
  const navigate = useNavigate();
  const config = useApi<BillingConfig>("/billing/config");
  const [session, setSession] = useState<CheckoutResponse | null>(null);
  const [checkout, setCheckout] = useState<StripeCheckoutElementsSdk | null>(null);
  const [ready, setReady] = useState(false);
  const [canConfirm, setCanConfirm] = useState(false);
  const [hasExpress, setHasExpress] = useState(false);
  const [email, setEmail] = useState("");
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const expressRef = useRef<HTMLDivElement>(null);
  const paymentRef = useRef<HTMLDivElement>(null);
  const itemKey = JSON.stringify(item);

  // New Checkout Session whenever what's being bought, the promo or the gift choice changes (each changes the order).
  useEffect(() => {
    let live = true;
    setSession(null); setReady(false); setError(null); onBusy(true);
    api.post<CheckoutResponse>("/checkout", { ...item, gift, promo_code: promo ?? undefined })
      .then((s) => { if (live) { setSession(s); onQuote(s.quote); } })
      .catch((e) => {
        if (!live) return;
        // A code that stopped working between apply and checkout (used up, expired): drop it and carry on at full price.
        if (promo) { onPromo(null); setError(e instanceof ApiError ? `${e.message} Removed the code.` : String(e)); return; }
        setError(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => live && onBusy(false));
    return () => { live = false; };
  }, [itemKey, gift, promo]);

  // Mount Stripe's elements for that session.
  useEffect(() => {
    const pk = config.data?.publishable_key;
    if (!session || !pk) return;
    let live = true;
    const cleanups: (() => void)[] = [];
    (async () => {
      const stripe = await getStripe(pk);
      if (!stripe || !live) return;
      const co = stripe.initCheckoutElementsSdk({ clientSecret: session.client_secret, elementsOptions: { appearance, fonts, loader: "never" } });
      co.on("change", (s) => setCanConfirm(s.canConfirm));
      const express = co.createExpressCheckoutElement({
        buttonHeight: 48, buttonTheme: { applePay: "white-outline", googlePay: "white" }, buttonType: { applePay: "buy", googlePay: "buy" },
        layout: { maxColumns: 3, maxRows: 1, overflow: "auto" }, paymentMethodOrder: undefined, paymentMethods: undefined,
      });
      express.on("ready", (e) => setHasExpress(!!e.availablePaymentMethods && Object.values(e.availablePaymentMethods).some(Boolean)));
      express.on("confirm", async (event) => {
        const actions = await co.loadActions();
        if (actions.type === "error") { setError(actions.error.message); return; }
        const res = await actions.actions.confirm({ expressCheckoutConfirmEvent: event, redirect: "if_required" });
        if (res.type === "error") setError(res.error.message);
        else navigate(`/checkout/complete?session_id=${session.session_id}`);
      });
      const payment = co.createPaymentElement({ layout: { type: "tabs" }, wallets: { applePay: "never", googlePay: "never" } });
      payment.on("ready", () => live && setReady(true));
      if (expressRef.current) express.mount(expressRef.current);
      if (paymentRef.current) payment.mount(paymentRef.current);
      cleanups.push(() => express.destroy(), () => payment.destroy());
      if (live) setCheckout(co);
    })().catch((e) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => { live = false; setCheckout(null); setHasExpress(false); cleanups.forEach((f) => f()); };
  }, [session?.client_secret, config.data?.publishable_key]);

  const pay = async (e: FormEvent) => {
    e.preventDefault();
    if (!checkout || !session) return;
    setPaying(true); setError(null);
    const actions = await checkout.loadActions();
    if (actions.type === "error") { setError(actions.error.message); setPaying(false); return; }
    const res = await actions.actions.confirm({ email: email.trim(), redirect: "if_required" });
    if (res.type === "error") { setError(res.error.message); setPaying(false); return; }
    navigate(`/checkout/complete?session_id=${session.session_id}`);
  };

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  // Hand the email to Stripe as soon as it looks valid, so Link and receipts don't ask for it again.
  useEffect(() => {
    if (!checkout || !emailOk) return;
    const id = setTimeout(async () => {
      const a = await checkout.loadActions();
      if (a.type === "success") await a.actions.updateEmail(email.trim());
    }, 400);
    return () => clearTimeout(id);
  }, [checkout, email, emailOk]);

  return (
    <form onSubmit={pay} className="pane hud relative overflow-hidden bg-surface/90 p-6 backdrop-blur-xl sm:p-8">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-2.5"><LogoMark className="size-7" /><Wordmark /></div>
        <span className="kicker flex items-center gap-1.5 text-ok"><Icon name="shield" className="size-3.5" />secure</span>
      </div>

      <div className={cn("transition-opacity", hasExpress ? "opacity-100" : "h-0 overflow-hidden opacity-0")}>
        <div ref={expressRef} />
        <div className="my-5 flex items-center gap-3 text-xs text-faint"><span className="h-px flex-1 bg-line" />or pay with card<span className="h-px flex-1 bg-line" /></div>
      </div>

      <div className="mb-4 rounded-lg border border-line bg-bg/40 px-3 py-2.5">
        <Switch checked={gift} onChange={onGift} label={<span>Buy as a gift <span className="text-faint">· get a key to send to someone</span></span>} />
      </div>
      <PromoField item={item} applied={promo} onApply={onPromo} />

      <label className="mb-4 block">
        <span className="mb-1.5 block text-xs font-medium text-muted">Email for your receipt</span>
        <Input type="email" required autoComplete="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" />
      </label>

      <div className="relative min-h-[180px]">
        {!ready && !error && (
          <div className="absolute inset-0 space-y-3">
            {[0, 1, 2].map((i) => <div key={i} className="h-11 animate-pulse rounded-[10px] bg-white/[0.04]" style={{ animationDelay: `${i * 120}ms` }} />)}
          </div>
        )}
        <div ref={paymentRef} className={cn("transition-opacity duration-500", ready ? "opacity-100" : "opacity-0")} />
      </div>

      <div className="mt-4"><ErrorText>{error}</ErrorText></div>

      <Button type="submit" variant="primary" size="lg" loading={paying} disabled={!ready || !canConfirm || !emailOk || paying}
        className="glow-border mt-5 h-13 w-full text-base">
        {paying ? "Processing…" : `Pay ${session ? fmtMoney(session.quote.amount_cents, session.quote.currency.toUpperCase()) : ""}`}
      </Button>

      <div className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px] text-faint">
        <span className="flex items-center gap-1"><Icon name="shield" className="size-3" />Encrypted by Stripe</span>
        <span>Card details never touch our servers</span>
      </div>
      {paying && <div className="pointer-events-none absolute inset-x-0 top-0 h-1/2 animate-scan bg-gradient-to-b from-transparent via-accent/10 to-transparent" />}
    </form>
  );
}

/** Checkout state shared by the summary and the payment panel. */
function useCheckoutState() {
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [gift, setGift] = useState(params.get("gift") === "1");
  const [promo, setPromo] = useState<string | null>(null);
  const [quote, setQuote] = useState<CheckoutQuote | null>(null);
  return { busy, setBusy, gift, setGift, promo, setPromo, quote, setQuote };
}

export function BundleCheckout() {
  const { bundleId = "" } = useParams();
  const bundle = useApi<Bundle>(`/bundles/${encodeURIComponent(bundleId)}`);
  const st = useCheckoutState();
  const b = bundle.data;
  if (bundle.error || (b && !b.purchasable)) {
    return (
      <div className="grid min-h-screen place-items-center px-6 text-center">
        <div><p className="mb-4 text-muted">{bundle.error?.message ?? `${b!.name} can't be bought right now: one of its games is off sale.`}</p><Link to="/pricing"><Button>Back to pricing</Button></Link></div>
      </div>
    );
  }
  const theme = themeFor(b?.games[0] ?? "", undefined);
  return (
    <div className="relative min-h-screen overflow-hidden bg-bg">
      <div className="pointer-events-none absolute -left-40 top-1/3 size-[36rem] rounded-full blur-[140px]" style={{ background: `${theme.a}22` }} />
      <div className="relative mx-auto grid min-h-screen max-w-6xl gap-10 px-6 py-10 lg:grid-cols-[1fr_minmax(0,460px)] lg:items-center lg:gap-16">
        {b ? <BundleSummary bundle={b} quote={st.quote} gift={st.gift} /> : <div />}
        {b && <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1, ease: EASE }}>
          <PaymentPanel item={{ bundle_id: b.id }} gift={st.gift} onGift={st.setGift} promo={st.promo} onPromo={st.setPromo} onQuote={st.setQuote} onBusy={st.setBusy} />
        </motion.div>}
      </div>
    </div>
  );
}

export default function Checkout() {
  const { gameId = "", tier = "0" } = useParams();
  const navigate = useNavigate();
  const game = useApi<Game>(`/games/${encodeURIComponent(gameId)}`);
  const st = useCheckoutState();
  const tierIndex = Math.max(0, Number.parseInt(tier, 10) || 0);
  const theme = useMemo(() => themeFor(gameId, game.data?.category), [gameId, game.data?.category]);

  if (game.error) {
    return (
      <div className="grid min-h-screen place-items-center px-6 text-center">
        <div><p className="mb-4 text-muted">{game.error.message}</p><Link to="/pricing"><Button>Back to pricing</Button></Link></div>
      </div>
    );
  }
  const g = game.data;
  // Never open a Checkout Session for a game that can't be bought; PaymentPanel is what creates one.
  if (g && !g.purchasable) {
    return (
      <div className="grid min-h-screen place-items-center bg-bg px-6 py-10">
        <div className="pane hud w-full max-w-md space-y-5 p-6 sm:p-8">
          <Link to={`/games/${g.id}`} className="inline-flex items-center gap-2 text-sm text-muted hover:text-fg"><Icon name="chevronLeft" className="size-4" />Back to {g.name}</Link>
          <div>
            <h1 className="text-2xl font-semibold leading-tight">{g.name}</h1>
            <CompatBadges game={g} className="mt-2.5" />
          </div>
          <NotForSale game={g} />
        </div>
      </div>
    );
  }
  return (
    <div className="relative min-h-screen overflow-hidden bg-bg">
      {g && <GameScene variant={variantFor(g.id, g.category)} accent={theme.a} accent2={theme.b} className="pointer-events-none absolute inset-y-0 left-0 w-full opacity-35 lg:w-1/2" />}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-bg/40 via-bg/80 to-bg" />
      <div className="pointer-events-none absolute -left-40 top-1/3 size-[36rem] rounded-full blur-[140px]" style={{ background: `${theme.a}22` }} />
      <div className="relative mx-auto grid min-h-screen max-w-6xl gap-10 px-6 py-10 lg:grid-cols-[1fr_minmax(0,460px)] lg:items-center lg:gap-16">
        {g ? <OrderSummary game={g} tierIndex={Math.min(tierIndex, g.pricing_tiers.length - 1)} busy={st.busy} quote={st.quote} gift={st.gift}
          onTier={(i) => navigate(`/checkout/${g.id}/${i}`, { replace: true })} /> : <div />}
        {g && <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1, ease: EASE }}>
          <PaymentPanel item={{ game_id: g.id, tier_index: Math.min(tierIndex, g.pricing_tiers.length - 1) }} gift={st.gift} onGift={st.setGift}
            promo={st.promo} onPromo={st.setPromo} onQuote={st.setQuote} onBusy={st.setBusy} />
        </motion.div>}
      </div>
    </div>
  );
}
